import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Newspaper, ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import type { PortfolioHolding } from '@/hooks/usePortfolioHoldings';

interface NewsItem {
  id: string;
  symbol: string;
  headline: string;
  summary: string;
  url: string;
  source: string;
  datetime: string;
  sentiment: number;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 0 || isNaN(diff)) return '';
  const min = Math.floor(diff / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

interface Props {
  holdings: PortfolioHolding[];
}

export function PortfolioNewsWidget({ holdings }: Props) {
  // Top 5 by market value (most relevant exposure)
  const topSymbols = [...holdings]
    .sort((a, b) => (b.market_value || 0) - (a.market_value || 0))
    .slice(0, 5)
    .map(h => h.symbol);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['portfolio-news', topSymbols.join(',')],
    queryFn: async (): Promise<NewsItem[]> => {
      const { data, error } = await supabase.functions.invoke('news-feed', {
        body: { symbols: topSymbols },
      });
      if (error) throw error;
      return (data?.news ?? []) as NewsItem[];
    },
    enabled: topSymbols.length > 0,
    staleTime: 4 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return (
    <Card className="bg-card border-[hsl(var(--card-border))]">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2 font-display">
          <Newspaper className="w-4 h-4 text-muted-foreground" />
          Portfolio News
          <Badge
            variant="outline"
            className="text-[10px] ml-auto border-[hsl(var(--card-border))] text-muted-foreground font-mono-data"
          >
            Top {topSymbols.length || 0} holdings
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 max-h-[28rem] overflow-y-auto">
        {holdings.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-6">
            Add holdings to see relevant news.
          </p>
        )}

        {holdings.length > 0 && isLoading && (
          <>
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
            <Skeleton className="h-14 w-full" />
          </>
        )}

        {holdings.length > 0 && !isLoading && isError && (
          <p className="text-xs text-muted-foreground text-center py-4">
            Couldn’t load news right now. Try again shortly.
          </p>
        )}

        {holdings.length > 0 && !isLoading && !isError && (data?.length ?? 0) === 0 && (
          <p className="text-xs text-muted-foreground text-center py-4">
            No recent news for your top holdings.
          </p>
        )}

        {(data ?? []).map(item => (
          <a
            key={item.id}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block p-3 rounded-md border border-transparent hover:border-[hsl(var(--card-border))] hover:bg-muted/30 transition-colors"
          >
            <div className="flex items-start gap-2.5">
              <Badge
                variant="outline"
                className="text-[10px] flex-shrink-0 font-mono-data border-[hsl(var(--card-border))] text-foreground"
              >
                {item.symbol}
              </Badge>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium leading-snug text-foreground line-clamp-2">
                  {item.headline}
                </p>
                <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1.5">
                  <span className="truncate">{item.source}</span>
                  <span>·</span>
                  <span className="font-mono-data">{relativeTime(item.datetime)}</span>
                  <ExternalLink className="w-2.5 h-2.5 ml-auto opacity-60" />
                </p>
              </div>
            </div>
          </a>
        ))}
      </CardContent>
    </Card>
  );
}

export default PortfolioNewsWidget;
